import { FilmstripGroup } from "./Filmstrip"
import { useTranslation } from "react-i18next"
import { Button } from "./ui/Button"
import { PresetsPanel } from "./PresetsPanel"
import { useAtom, useAtomValue } from "jotai"
import { Gallery } from "./Gallery"
import { store } from "@/store/store"
import { AdjustmentsPanel } from "./AdjustmentsPanel"
import {
	hasAdjustmentsToCopyAtom,
	hasAdjustmentsToPasteAtom,
	pasteAdjustments,
	resetCurrentHistoryRecord,
	showCopySettings,
	undoableAdjustmentsHistoryAtom,
	setHistoryIndex,
} from "@/store/store-history"
import { CatalogManager } from "@/lib/catalog"
import { Editor } from "@/components/Editor"
import { HistoryPanel } from "./HistoryPanel"
import { ControlGroup } from "./ControlGroup"
import { StyleControls } from "./StyleControls"
import { Icon } from "./icons/Icon"
import { InfoPanel } from "./InfoPanel"
import {
	openBasicEditView,
	openCompareView,
	openFocusView,
	openGridView,
	gridColAtom,
	photosViewAtom,
	toggleCropView,
	toggleHealingView,
	toggleMaskView,
} from "@/store/photo/photos-view-state"
import { useViewTransition } from "@/utils/useViewTransition"
import {
	currentPhotoAtom,
	displayedPhotosAtom,
	selectedPhotosAtom,
	selectedPhotosIDsAtom,
} from "@/store/photo/photos-state"
import { hideSelectedPhotos, unhideSelectedPhotos } from "@/store/photo/store-photos"
import {
	isFullscreenAtom,
	isPhotoFullscreenAtom,
	leftExpandedPanelAtom,
	projectActionToastAtom,
	exitPhotoFullscreen,
	showingCropPanelAtom,
	showingHealingPanelAtom,
	showingLeftPanelAtom,
	showingMaskingPanelAtom,
	showingRightPanelAtom,
	toggleFullScreen,
	togglePhotoFullScreen,
	toggleLeftPanel,
	toggleRightPanel,
} from "@/store/ui-views-state"
import {
	batchLock,
	batchMarkReference,
	batchRemoveReference,
	batchUnlock,
} from "@/store/style/reference"
import {
	nextCluster,
	nextPhoto,
	prevCluster,
	prevPhoto,
	selectPhoto,
} from "@/store/photo/photos-selection-state"
import { Toolbar } from "./Toolbar"
import { BottomToolbar } from "./BottomToolBar"
import { ScrollBar } from "@/components/ui/ScrollBar"
import toast from "react-hot-toast"
import { useEditor } from "@/utils/useEditor"
import { Suspense, useEffect, useRef, useState } from "react"
import { PhotoContextMenu } from "@/components/PhotoContextMenu"
import { showContextMenu } from "@/components/ContextMenuManager"
import { ContextMenuItem } from "@/components/ui/ContextMenu"
import { highlightsClippingAtom, shadowsClippingAtom } from "@/store/settings-state"
import { usePreventPageUnload } from "../utils/usePreventPageUnload"
import {
	rateSelectedPhotos,
	setColorTag,
	removeSelectedPhotos,
	setFlag,
} from "@/store/photo/store-photos"
import { selectedHealingAtom } from "@/store/editor/healing-state"
import { AddMaskGroupWithMask } from "@/store/editor/masks-state"
import {
	createBackgroundMask,
	createBrushMask,
	createGradientMask,
	createRadialMask,
	createSkyMask,
	createSubjectMask,
} from "@/store/editor/mask-utils"
import { commandOrCtrl } from "@/utils/system"
import { useShortcuts } from "@/utils/useShortcuts"
import { getColorFromShortcut } from "@/utils/exif"
import { isCullingModeAtom, isManualModeAtom, projectAtom } from "@/store/project/projects-state"
import { useAreMasksSupported, useIsHealingSupported } from "@/store/app/browser-capabilities"
import { pushDialog } from "@/store/dialogs-state"
import { MasksUnavailableDialog } from "@/components/Dialogs/MasksUnavailableDialog"
import { HealingUnavailableDialog } from "@/components/Dialogs/HealingUnavailableDialog"
import { Histogram } from "./Histogram"
import { FacesPanel } from "./FacesPanel"
import {
	currentClusterPhotosAtom,
	isShowingRejectsAtom,
	isShowingClustersAtom,
	isSuggestingKeepersAtom,
	isSuggestingKeepersOrRejectsAtom,
	setManualCullingTagForPhotos,
} from "@/store/importing/culling-state"
import {
	CullingAutoAdvance,
	CullingGroup,
	CullingRejects,
	CullingSuggestion,
} from "./CullingPanels"
import { MemoryUtilisationPanel } from "@/components/Debug/MemoryUtilisationPanel"
import { AIDebugInfoPanel } from "@/components/Debug/AIDebugInfoPanel"
import { isAIDebugEnabled, isPerformanceDebugEnabled } from "@/utils/measurement/feature-flag"
import { TaskPerformancePanel } from "./Debug/TaskPerformancePanel"
import { ExpandablePanel } from "@/store/undoable-state"
import { ColorMatchSamplesPanel } from "./ColorMatchSamplesPanel"
import { isCurrentFileLoaded, loadCurrentFile } from "@/store/editor/photo-loading"
import clsx from "clsx"
import { PanelHandle } from "./PanelHandle"
import {
	actionShortcuts,
	eventToActionKey,
	cullingKeyToValue,
	flagKeyToValue,
	maskTypeKeyToValue,
} from "@/shortcuts/shortcuts"
import { isFitZoom, toggleAutoZoom } from "@/store/editor/editor-zoom"

function Project() {
	const { t } = useTranslation("components")
	const [project] = useAtom(projectAtom)
	const [previewState] = useAtom(photosViewAtom)
	const [currentPhoto] = useAtom(currentPhotoAtom)
	const [selectedPhotos, setSelectedPhotos] = useAtom(selectedPhotosAtom)
	const shouldShowLeftPanel = useAtomValue(showingLeftPanelAtom)
	const shouldShowRightPanel = useAtomValue(showingRightPanelAtom)
	const leftExpandedPanel = useAtomValue(leftExpandedPanelAtom)
	const [hasAdjustmentsToCopy] = useAtom(hasAdjustmentsToCopyAtom)
	const [hasAdjustmentsToPaste] = useAtom(hasAdjustmentsToPasteAtom)
	const [highlightsClipping, setHighlightsClipping] = useAtom(highlightsClippingAtom)
	const [shadowsClipping, setShadowClipping] = useAtom(shadowsClippingAtom)
	const showingHealing = useAtomValue(showingHealingPanelAtom)
	const isPhotoFullscreen = useAtomValue(isPhotoFullscreenAtom)

	const isManualMode = useAtomValue(isManualModeAtom)
	const isCullingMode = useAtomValue(isCullingModeAtom)
	const isShowingCluster = useAtomValue(isShowingClustersAtom)
	const isShowingKeepersOrRejects = useAtomValue(isSuggestingKeepersOrRejectsAtom)
	const isShowingKeepers = useAtomValue(isSuggestingKeepersAtom)
	const isShowingRejects = useAtomValue(isShowingRejectsAtom)

	const [isCropActive] = useAtom(showingCropPanelAtom)
	const [isMaskActive] = useAtom(showingMaskingPanelAtom)
	const isCompareEnabled = !isCullingMode && previewState == "focus" && !isCropActive
	const isHealingActive = useAtomValue(selectedHealingAtom) !== null
	const areMasksSupported = useAreMasksSupported()
	const isHealingSupported = useIsHealingSupported()

	const { shouldMount, currentView } = useViewTransition()
	const shouldMountGrid = shouldMount("grid")
	const showProjectUI = !isPhotoFullscreen
	const gridLayerClassName = isPhotoFullscreen
		? "pointer-events-none opacity-0"
		: currentView === "grid"
			? "visible opacity-100"
			: "invisible"
	const showEditorLayer = isPhotoFullscreen || previewState !== "grid"
	const showLeftPanel = showProjectUI && shouldShowLeftPanel
	const showRightPanel = showProjectUI && shouldShowRightPanel
	const showRightExpandedPanel = showRightPanel ? leftExpandedPanel : null

	// Action Toasts
	const [toastTimer, setToastTimer] = useState<NodeJS.Timeout | null>(null)
	const [actionToast] = useAtom(projectActionToastAtom)

	const lastArrowNavigationFrame = useRef(0)
	const wasArrowNavigationRepeated = useRef(false)
	const loadFullSizeTimeout = useRef<NodeJS.Timeout | null>(null)
	const photoFullscreenRestoreFrame = useRef<number | null>(null)

	const editor = useEditor()
	const history = useAtomValue(undoableAdjustmentsHistoryAtom)

	const handleTogglePhotoFullscreen = () => {
		const isEnteringPhotoFullscreen = !store.get(isPhotoFullscreenAtom)

		if (photoFullscreenRestoreFrame.current !== null) {
			cancelAnimationFrame(photoFullscreenRestoreFrame.current)
			photoFullscreenRestoreFrame.current = null
		}

		if (isEnteringPhotoFullscreen) {
			editor.setViewMode("after")

			if (!isCurrentFileLoaded()) {
				editor.clearViewport()
				void loadCurrentFile({ kinds: ["preview", "full"] })
			}
		}

		togglePhotoFullScreen()
	}

	const handleArrowNavigation = (event: KeyboardEvent, updateSelectedPhoto: () => void) => {
		if (!event.repeat) {
			if (event.type === "keyup") {
				if (wasArrowNavigationRepeated.current == true) {
					loadCurrentFile({ kinds: ["preview", "full"] })
				} else {
					if (loadFullSizeTimeout.current) {
						clearTimeout(loadFullSizeTimeout.current)
					}

					// Classical debounce applied to keyboard arrow navigation.
					// <= 350ms is when we consider user continuously switching photos.
					// > 350ms is when we consider user is actually focusing on the current photo.
					// The value was inferred empirically.
					loadFullSizeTimeout.current = setTimeout(() => {
						loadCurrentFile({ kinds: ["full"] })
					}, 350)
				}

				wasArrowNavigationRepeated.current = false
			} else if (event.type === "keydown") {
				updateSelectedPhoto()
				loadCurrentFile({ kinds: ["preview"] })
			}
			return
		}

		wasArrowNavigationRepeated.current = true

		requestAnimationFrame((now) => {
			if (now - lastArrowNavigationFrame.current < 1000 / 20) return
			lastArrowNavigationFrame.current = now

			if (!wasArrowNavigationRepeated.current) {
				return
			}

			updateSelectedPhoto()
			loadCurrentFile({ kinds: ["preview"], isTransient: true })
		})
	}

	const navigateGridByRowOffset = (direction: -1 | 1) => {
		if (previewState !== "grid") {
			return
		}

		const gridCol = store.get(gridColAtom)
		if (!gridCol || gridCol <= 0) {
			return
		}

		const photos = store.get(displayedPhotosAtom)
		if (photos.length === 0) {
			return
		}

		const current = store.get(currentPhotoAtom)
		const currentIndex = current ? photos.findIndex((photo) => photo.id === current.id) : -1

		if (currentIndex < 0) {
			photos[0] && selectPhoto(photos[0])
			return
		}

		let targetIndex = currentIndex + direction * gridCol
		if (direction === -1) {
			targetIndex = Math.max(0, targetIndex)
		} else {
			targetIndex = Math.min(photos.length - 1, targetIndex)
		}

		if (targetIndex === currentIndex) {
			return
		}

		const targetPhoto = photos[targetIndex]
		if (targetPhoto) {
			selectPhoto(targetPhoto)
		}
	}

	const handleHorizontalNavigation = (event: KeyboardEvent) => {
		const isPrev = event.key === "ArrowLeft"
		handleArrowNavigation(event, () => {
			isPrev ? prevPhoto() : nextPhoto()
		})
	}

	const handleEditMoreContextMenu = (e: React.MouseEvent) => {
		e.stopPropagation()

		const button = e.currentTarget
		const rect = button.getBoundingClientRect()

		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const modifiedEvent = {
			...e,
			clientX: Math.round(rect.right) + 16,
			// Menu positioned above button: 4 items (28px each) + padding (10px) + margin (8px) = 130px
			clientY: Math.round(rect.top) - 130,
			preventDefault: () => e.preventDefault(),
			stopPropagation: () => e.stopPropagation(),
		} as React.MouseEvent

		showContextMenu(
			modifiedEvent,
			<div aria-label={t("project.ariaEditMoreMenu")} className="w-[180px]">
				<ContextMenuItem
					trackingLabel="history_undo"
					disabled={!history?.canUndo}
					onClick={() => setHistoryIndex("undo")}
				>
					<div className="flex w-full items-center justify-between">
						<span>{t("project.undo")}</span>
						<div className="flex items-center gap-1">
							<KeyboardKey>{commandOrCtrl}</KeyboardKey>
							<KeyboardKey>Z</KeyboardKey>
						</div>
					</div>
				</ContextMenuItem>

				<ContextMenuItem
					trackingLabel="history_redo"
					disabled={!history?.canRedo}
					onClick={() => setHistoryIndex("redo")}
				>
					<div className="flex w-full items-center justify-between">
						<span>{t("project.redo")}</span>
						<div className="flex items-center gap-1">
							<KeyboardKey>{commandOrCtrl}</KeyboardKey>
							<KeyboardKey>SHIFT</KeyboardKey>
							<KeyboardKey>Z</KeyboardKey>
						</div>
					</div>
				</ContextMenuItem>

				<ContextMenuItem
					trackingLabel="copy_edits"
					onClick={showCopySettings}
					disabled={!hasAdjustmentsToCopy}
				>
					<div className="flex w-full items-center justify-between">
						<span>{t("project.copyEdits")}</span>
						<div className="flex items-center gap-1">
							<KeyboardKey>{commandOrCtrl}</KeyboardKey>
							<KeyboardKey>C</KeyboardKey>
						</div>
					</div>
				</ContextMenuItem>

				<ContextMenuItem
					trackingLabel="paste_edits"
					onClick={pasteAdjustments}
					disabled={!hasAdjustmentsToPaste || selectedPhotos.length === 0}
				>
					<div className="flex w-full items-center justify-between">
						<span>{t("project.pasteEdits")}</span>
						<div className="flex items-center gap-1">
							<KeyboardKey>{commandOrCtrl}</KeyboardKey>
							<KeyboardKey>V</KeyboardKey>
						</div>
					</div>
				</ContextMenuItem>
			</div>,
		)
	}

	usePreventPageUnload(() => CatalogManager.flushDatabaseChanges())

	useEffect(() => {
		return () => {
			editor.clearViewport()
		}
	}, [editor])

	useEffect(() => {
		if (toastTimer) {
			clearTimeout(toastTimer)
		}
		if (actionToast) {
			toast.custom(<ToastView content={actionToast.content} />, {
				id: "action",
			})
			setToastTimer(
				setTimeout(() => {
					toast.remove("action")
				}, 2000),
			)
		} else {
			toast.remove("action")
		}
	}, [actionToast]) // eslint-disable-line react-hooks/exhaustive-deps

	useShortcuts({
		keys: [
			actionShortcuts.rate0,
			actionShortcuts.rate1,
			actionShortcuts.rate2,
			actionShortcuts.rate3,
			actionShortcuts.rate4,
			actionShortcuts.rate5,
		],
		action: (event) => {
			switch (event.key) {
				case actionShortcuts.rate0:
					rateSelectedPhotos(0)
					break
				case actionShortcuts.rate1:
					rateSelectedPhotos(1)
					break
				case actionShortcuts.rate2:
					rateSelectedPhotos(2)
					break
				case actionShortcuts.rate3:
					rateSelectedPhotos(3)
					break
				case actionShortcuts.rate4:
					rateSelectedPhotos(4)
					break
				case actionShortcuts.rate5:
					rateSelectedPhotos(5)
					break
			}
		},
		trackingLabel: "rate_photo",
		trackingValue: (event) => event.key,
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [
			actionShortcuts.colorTagRed,
			actionShortcuts.colorTagYellow,
			actionShortcuts.colorTagGreen,
			actionShortcuts.colorTagBlue,
		],
		action: (event) => {
			const color = getColorFromShortcut(event.key)
			if (!color) {
				return
			}
			setColorTag(color)
		},
		trackingLabel: "set_color_tag",
		trackingValue: (event) => {
			const color = getColorFromShortcut(event.key) ?? "Unknown"
			return color
		},
		allowInPhotoFullscreen: true,
	})
	useShortcuts({
		keys: [actionShortcuts.gridView],
		action: openGridView,
		trackingLabel: "open_gallery",
	})

	useShortcuts({
		keys: [actionShortcuts.focusView],
		action: openFocusView,
		trackingLabel: "open_focus",
	})

	useShortcuts({
		keys: [actionShortcuts.compareView],
		action: openCompareView,
		trackingLabel: "open_compare",
		disabled: isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.toggleCrop],
		action: toggleCropView,
		trackingLabel: isCropActive ? "close_crop" : "open_crop",
		disabled: isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.toggleHealing],
		action: () => {
			if (!isHealingSupported) {
				pushDialog(HealingUnavailableDialog)
				return
			}
			toggleHealingView()
		},
		trackingLabel: isHealingActive ? "close_healing" : "open_healing",
		disabled: isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.toggleMask],
		action: () => {
			if (!areMasksSupported) {
				pushDialog(MasksUnavailableDialog)
				return
			}
			toggleMaskView()
		},
		trackingLabel: isMaskActive ? "close_mask" : "open_mask",
		disabled: isCullingMode,
	})

	useShortcuts({
		keys: ["e"],
		action: openBasicEditView,
		trackingLabel: "open_basic_edit",
		disabled: isCullingMode || previewState !== "grid",
	})

	useShortcuts({
		keys: [
			actionShortcuts.subjectMask,
			actionShortcuts.backgroundMask,
			actionShortcuts.skyMask,
			actionShortcuts.brushMask,
			actionShortcuts.gradientMask,
			actionShortcuts.radialMask,
		],
		action: (event) => {
			if (!areMasksSupported) {
				pushDialog(MasksUnavailableDialog)
				return
			}
			if (!isMaskActive) {
				toggleMaskView()
			}
			const shortcut = eventToActionKey(event)
			switch (shortcut) {
				case actionShortcuts.subjectMask:
					void AddMaskGroupWithMask(createSubjectMask())
					break
				case actionShortcuts.backgroundMask:
					void AddMaskGroupWithMask(createBackgroundMask())
					break
				case actionShortcuts.skyMask:
					void AddMaskGroupWithMask(createSkyMask())
					break
				case actionShortcuts.brushMask:
					void AddMaskGroupWithMask(createBrushMask())
					break
				case actionShortcuts.gradientMask:
					void AddMaskGroupWithMask(createGradientMask())
					break
				case actionShortcuts.radialMask:
					void AddMaskGroupWithMask(createRadialMask())
					break
			}
		},
		trackingLabel: "add_mask",
		trackingValue: (event) => {
			return event.key in maskTypeKeyToValue
				? maskTypeKeyToValue[event.key as keyof typeof maskTypeKeyToValue]
				: "Unknown"
		},
		preventDefault: true,
		disabled: isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.markReference],
		action: batchMarkReference,
		trackingLabel: "mark_reference",
		trackingValues: (_) => ({ projectId: project?.id, photoCount: selectedPhotos.length }),
		disabled: isManualMode || isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.lockPhoto],
		action: batchLock,
		trackingLabel: "lock_photo",
		disabled: isManualMode || isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.removeReference],
		action: batchRemoveReference,
		trackingLabel: "remove_reference",
		trackingValues: (_) => ({ projectId: project?.id, photoCount: selectedPhotos.length }),
		preventDefault: true,
		disabled: isManualMode || isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.unlockPhoto],
		action: batchUnlock,
		trackingLabel: "unlock_photo",
		preventDefault: true,
		disabled: isManualMode || isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.del, actionShortcuts.backspace, actionShortcuts.delete],
		action: removeSelectedPhotos,
		trackingLabel: "delete_photo",
		disabled: showingHealing || isMaskActive,
	})

	useShortcuts({
		keys: [actionShortcuts.hidePhoto],
		action: () => void hideSelectedPhotos(),
		trackingLabel: "hide_photo",
	})

	useShortcuts({
		keys: [actionShortcuts.unhidePhoto],
		action: () => void unhideSelectedPhotos(),
		trackingLabel: "unhide_photo",
		preventDefault: true,
	})

	useShortcuts({
		keys: [actionShortcuts.rejected],
		action: () => {
			setFlag([...store.get(selectedPhotosIDsAtom)], flagKeyToValue[actionShortcuts.rejected])
		},
		trackingLabel: "reject_photo",
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [actionShortcuts.flagged],
		action: () => {
			setFlag([...store.get(selectedPhotosIDsAtom)], flagKeyToValue[actionShortcuts.flagged])
		},
		trackingLabel: "flag_photo",
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [actionShortcuts.unflagged],
		action: () => {
			setFlag(
				[...store.get(selectedPhotosIDsAtom)],
				flagKeyToValue[actionShortcuts.unflagged],
			)
		},
		trackingLabel: "unflag_photo",
		allowInPhotoFullscreen: true,
	})

	const isHorizontalNavigationDisabled = isHealingActive || isCropActive || isPhotoFullscreen

	useShortcuts({
		keys: [actionShortcuts.cullingKeeper],
		action: () => {
			void setManualCullingTagForPhotos(
				[...store.get(selectedPhotosIDsAtom)],
				cullingKeyToValue[actionShortcuts.cullingKeeper],
			)
		},
		trackingLabel: "mark_keeper",
		disabled: !isCullingMode || !isShowingKeepers,
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [actionShortcuts.cullingReject],
		action: () => {
			void setManualCullingTagForPhotos(
				[...store.get(selectedPhotosIDsAtom)],
				cullingKeyToValue[actionShortcuts.cullingReject],
			)
		},
		trackingLabel: "mark_reject",
		disabled: !isCullingMode || !isShowingRejects,
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [actionShortcuts.cullingCandidate],
		action: () => {
			void setManualCullingTagForPhotos(
				[...store.get(selectedPhotosIDsAtom)],
				cullingKeyToValue[actionShortcuts.cullingCandidate],
			)
		},
		trackingLabel: "mark_candidate",
		disabled: !isCullingMode || !isShowingKeepersOrRejects,
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [actionShortcuts.cullingClear],
		action: () => {
			void setManualCullingTagForPhotos([...store.get(selectedPhotosIDsAtom)], null)
		},
		trackingLabel: "clear_culling_tag",
		disabled: !isCullingMode || !isShowingKeepersOrRejects,
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: ["ArrowLeft", "ArrowRight"],
		keydown: true,
		keyup: true,
		action: handleHorizontalNavigation,
		trackingLabel: "navigate_grid_horizontal",
		disabled: isHorizontalNavigationDisabled || previewState !== "grid",
		preventDefault: true,
	})

	useShortcuts({
		keys: ["ArrowLeft", "ArrowRight"],
		keydown: true,
		keyup: true,
		action: handleHorizontalNavigation,
		trackingLabel: "prev_next_photo",
		disabled: isHorizontalNavigationDisabled || previewState === "grid",
		preventDefault: true,
		disableTracking: true,
	})

	useShortcuts({
		keys: ["ArrowLeft", "ArrowRight"],
		keydown: true,
		keyup: true,
		action: handleHorizontalNavigation,
		trackingLabel: "photo_fullscreen_prev_next_photo",
		disabled: !isPhotoFullscreen || !currentPhoto,
		preventDefault: true,
		allowInPhotoFullscreen: true,
	})

	const isArrowUpDisabled = isHealingActive || isCropActive || isPhotoFullscreen
	const isArrowDownDisabled = isHealingActive || isCropActive || isPhotoFullscreen

	useShortcuts({
		keys: ["ArrowUp", "ArrowDown"],
		keydown: true,
		keyup: true,
		action: (event) => {
			const isPrev = event.key === "ArrowUp"

			if (isShowingCluster) {
				handleArrowNavigation(event, () => {
					isPrev ? prevCluster() : nextCluster()
				})
				return
			}

			if (previewState === "grid") {
				event.preventDefault()
				handleArrowNavigation(event, () => navigateGridByRowOffset(isPrev ? -1 : 1))
			}
		},
		trackingLabel: isShowingCluster ? "prev_next_cluster" : "navigate_grid_vertical",
		disabled: isArrowUpDisabled || isArrowDownDisabled,
	})

	useShortcuts({
		keys: [actionShortcuts.copyAdjustments],
		action: showCopySettings,
		trackingLabel: "copy_adjustments",
		disabled: isCullingMode || !hasAdjustmentsToCopy,
		preventDefault: true,
	})

	useShortcuts({
		keys: [actionShortcuts.pasteAdjustments],
		action: pasteAdjustments,
		trackingLabel: "paste_adjustments",
		disabled: isCullingMode || selectedPhotos.length == 0 || !hasAdjustmentsToPaste,
		preventDefault: true,
	})

	useShortcuts({
		keys: [actionShortcuts.selectAll],
		action: () => {
			if (isShowingCluster) {
				setSelectedPhotos(store.get(currentClusterPhotosAtom))
			} else {
				setSelectedPhotos(store.get(displayedPhotosAtom))
			}
		},
		trackingLabel: "select_all",
		preventDefault: true,
	})

	useShortcuts({
		keys: [actionShortcuts.togglePhotoFullScreen],
		action: handleTogglePhotoFullscreen,
		trackingLabel: "toggle_photo_fullscreen",
		keydown: false,
		keyup: true,
		disabled: !currentPhoto,
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [actionShortcuts.toggleFullScreen],
		action: toggleFullScreen,
		trackingLabel: "toggle_fullscreen",
		keydown: false,
		keyup: true,
		allowInPhotoFullscreen: true,
	})

	useEffect(() => {
		const handleFullscreenChange = () => {
			if (!document.fullscreenElement && store.get(isFullscreenAtom) != false) {
				store.set(isFullscreenAtom, false)
			}
		}

		document.addEventListener("fullscreenchange", handleFullscreenChange, false)

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange, false)
		}
	}, [])

	useEffect(() => {
		return () => {
			if (photoFullscreenRestoreFrame.current !== null) {
				cancelAnimationFrame(photoFullscreenRestoreFrame.current)
			}
		}
	}, [])

	useEffect(() => {
		if (!isPhotoFullscreen) {
			return
		}

		editor.setViewMode("after")

		return () => {
			if (store.get(photosViewAtom) === "before/after") {
				photoFullscreenRestoreFrame.current = requestAnimationFrame(() => {
					photoFullscreenRestoreFrame.current = null
					if (store.get(isPhotoFullscreenAtom)) {
						return
					}

					editor.setViewMode("compare")
				})
			}
		}
	}, [editor, isPhotoFullscreen])

	useShortcuts({
		keys: [actionShortcuts.showBefore],
		action: (event) => {
			if (event.type === "keydown") {
				editor.setViewMode("before")
			} else {
				editor.setViewMode("after")
			}
		},
		trackingLabel: "view_before_after",
		disabled: !isCompareEnabled,
		keyup: true,
		keydown: true,
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [actionShortcuts.toggleHighlightShadowClipping],
		action: () => {
			if (highlightsClipping && shadowsClipping) {
				setHighlightsClipping(false)
				setShadowClipping(false)
			} else {
				setHighlightsClipping(true)
				setShadowClipping(true)
			}
		},
		trackingLabel: "toggle_highlight_shadow_clipping",
		disabled: previewState !== "focus" || isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.enter],
		action: () => {
			if (previewState === "grid") {
				openFocusView()
				if (!isFitZoom(editor)) {
					toggleAutoZoom(editor)
				}
			} else if (previewState === "focus") {
				if (isFitZoom(editor)) {
					toggleAutoZoom(editor)
				} else {
					openGridView()
				}
			}
		},
		trackingLabel: "enter_key_action",
		disabled: !isCullingMode,
	})

	useShortcuts({
		keys: [actionShortcuts.escape],
		action: exitPhotoFullscreen,
		trackingLabel: "exit_photo_fullscreen",
		disabled: !isPhotoFullscreen,
		preventDefault: true,
		allowInPhotoFullscreen: true,
	})

	useShortcuts({
		keys: [actionShortcuts.escape],
		action: () => {
			if (previewState === "focus") {
				if (isFitZoom(editor)) {
					openGridView()
				} else {
					toggleAutoZoom(editor)
				}
			}
		},
		trackingLabel: "escape_key_action",
		disabled: !isCullingMode || isPhotoFullscreen,
	})

	// Views

	const renderRightExpandedPanel = (panel: ExpandablePanel) => {
		return (
			<div
				role="region"
				aria-label={t("project.ariaExpandedPanel")}
				className="scrollarea no-scrollbar flex h-full w-[284px] flex-none flex-col overflow-y-scroll border-r border-base-700 bg-base-800"
			>
				{panel === "colormatch_samples" && (
					<Suspense fallback={<div className="size-full shrink-0 bg-base-800"></div>}>
						<ColorMatchSamplesPanel />
					</Suspense>
				)}
				<ScrollBar visible="hover" />
			</div>
		)
	}

	const renderLeftToolbar = () => {
		if (isCullingMode) {
			return (
				<div
					role="region"
					aria-label={t("project.ariaLeftCullingPanelGroup")}
					className="scrollarea no-scrollbar flex h-full w-[284px] flex-none flex-col overflow-y-scroll border-r border-base-900 bg-base-800"
				>
					<InfoPanel />

					<ControlGroup
						name={t("project.panelHistogram")}
						expandKey="histogram"
						isExpanded
						disabled={false}
					>
						<div className="flex w-full flex-col pt-1">
							<Histogram />
						</div>
					</ControlGroup>

					<ControlGroup
						expandKey="smart_culling"
						name={t("project.panelSmartCulling")}
						isExpanded
					>
						<CullingGroup isSetup={false} />
						<CullingRejects isSetup={false} />
						<CullingSuggestion isSetup={false} />
						<CullingAutoAdvance />
					</ControlGroup>
					{isPerformanceDebugEnabled() && <MemoryUtilisationPanel />}
					{isPerformanceDebugEnabled() && <TaskPerformancePanel />}

					<ScrollBar visible="hover" />
				</div>
			)
		} else {
			return (
				<div className="flex h-full w-[284px] flex-none flex-col border-r border-base-900 bg-base-800">
					<div
						role="region"
						aria-label={t("project.ariaEditorLeftPanelGroup")}
						className="scrollarea flex grow flex-col items-center justify-start overflow-y-scroll"
					>
						<InfoPanel />
						<PresetsPanel />

						<ControlGroup
							isExpanded={true}
							expandKey="history"
							name={t("project.panelHistory")}
							hideSeparator={true}
						>
							<HistoryPanel />
						</ControlGroup>

						{isAIDebugEnabled() && <AIDebugInfoPanel />}
						{isPerformanceDebugEnabled() && <MemoryUtilisationPanel />}
						{isPerformanceDebugEnabled() && <TaskPerformancePanel />}

						<ScrollBar visible="hover" />
					</div>
				</div>
			)
		}
	}

	const WIDTH_DEFAULT = 284
	const WIDTH_MAX = 960
	const WIDTH_MIN = 256
	const [rightPanelWidth, setRightPanelWidth] = useState(WIDTH_DEFAULT)
	const rightPanelScrollRef = useRef<HTMLDivElement>(null)

	const handleDragHandle = (delta: number) => {
		const w = rightPanelWidth - delta
		if (w <= WIDTH_MAX && w >= WIDTH_MIN) {
			setRightPanelWidth(w)
		}
	}

	const renderRightToolbar = () => {
		if (isCullingMode) {
			return project ? (
				<div className="flex flex-row">
					<PanelHandle
						label={t("project.labelRightPanelGroupResizer")}
						onDrag={handleDragHandle}
					/>{" "}
					<div
						role="region"
						aria-label={t("project.ariaRightCullingPanelGroup")}
						className="scrollarea no-scrollbar flex h-full w-[284px] flex-none flex-col overflow-y-scroll bg-base-800"
						style={{ width: rightPanelWidth }}
						ref={rightPanelScrollRef}
					>
						<FacesPanel
							currentPhoto={currentPhoto}
							project={project}
							width={rightPanelWidth}
							scrollContainerRef={rightPanelScrollRef}
						/>

						<ScrollBar visible="hover" />
					</div>
				</div>
			) : null
		} else {
			return (
				<div className="flex h-full w-[284px] flex-none flex-col border-l border-base-900 bg-base-800">
					{currentPhoto && !isManualMode && <StyleControls />}
					<AdjustmentsPanel />
					<div className="mt-auto flex items-center justify-between border-t border-base-900 p-2">
						<Button
							trackingLabel="reset_adjustments"
							aria-label={t("project.ariaResetAdjustments")}
							className="inline-flex flex-1"
							size="sm"
							onClick={() => {
								if (currentPhoto) {
									void resetCurrentHistoryRecord()
								}
							}}
						>
							{t("project.resetAll")}
						</Button>

						<Button
							aria-label={t("project.ariaOpenUndoRedoCopyPasteActionsList")}
							size="sm"
							className="ml-2"
							trackingLabel="edit_more"
							onContextMenu={handleEditMoreContextMenu}
							onClick={handleEditMoreContextMenu}
						>
							<Icon name="more" />
						</Button>
					</div>
				</div>
			)
		}
	}

	const renderLeftToolbarExpander = () => {
		return (
			<div
				role="button"
				aria-label={t("project.ariaToggleLeftPanelGroup")}
				className="flex h-full w-4 items-center border-r border-base-900 bg-base-800 hover:bg-base-700"
				onClick={toggleLeftPanel}
			>
				<Icon size={14} name={shouldShowLeftPanel ? "left" : "right"} />
			</div>
		)
	}

	const renderRightToolbarExpander = () => {
		return (
			<div
				role="button"
				aria-label={t("project.ariaToggleRightPanelGroup")}
				className="flex h-full w-4 items-center border-l border-base-900 bg-base-800 hover:bg-base-700"
				onClick={toggleRightPanel}
			>
				<Icon size={14} name={shouldShowRightPanel ? "right" : "left"} />
			</div>
		)
	}

	return (
		<div
			aria-label={
				isPhotoFullscreen
					? t("project.ariaPhotoFullscreenViewport")
					: t("project.ariaProject")
			}
			className="flex size-full flex-col overflow-hidden"
		>
			{showProjectUI && <Toolbar />}

			<div className="relative flex size-full flex-col overflow-hidden">
				<div
					className={clsx(
						"flex size-full flex-1 flex-row justify-between overflow-hidden",
						showProjectUI && "min-w-[1000px]",
					)}
				>
					{showProjectUI && renderLeftToolbarExpander()}
					{showLeftPanel && renderLeftToolbar()}
					<div className="relative flex flex-1 flex-col overflow-hidden">
						{isCullingMode && shouldMountGrid && (
							<div
								className={clsx(
									gridLayerClassName,
									"absolute inset-0 z-[12] flex size-full flex-1 flex-col",
								)}
							>
								<Gallery />
							</div>
						)}
						<div
							className={clsx(
								"absolute inset-0 flex size-full flex-1 flex-col",
								showEditorLayer ? "visible" : "invisible",
							)}
						>
							<div
								className="flex flex-1 flex-col"
								onContextMenu={
									showingHealing
										? undefined
										: (e) => showContextMenu(e, <PhotoContextMenu />)
								}
							>
								<Editor />
							</div>
							<div className={clsx(!showProjectUI && "hidden")}>
								<FilmstripGroup />
							</div>
						</div>
					</div>

					{showRightExpandedPanel && renderRightExpandedPanel(showRightExpandedPanel)}
					{showRightPanel && renderRightToolbar()}
					{showProjectUI && renderRightToolbarExpander()}
				</div>

				{!isCullingMode && shouldMountGrid && (
					<div
						className={clsx(
							gridLayerClassName,
							"absolute inset-0 z-[12] flex size-full flex-1 flex-col",
						)}
					>
						<Gallery />
					</div>
				)}
			</div>

			{showProjectUI && <BottomToolbar />}
		</div>
	)
}

export default Project

const KeyboardKey = ({
	children,
	className = "",
}: {
	children: React.ReactNode
	className?: string
}) => {
	const baseClass = "flex items-center justify-center bg-base-600 rounded-sm px-1 py-px"
	return <span className={`${baseClass} ${className}`}>{children}</span>
}

export const ToastView = (props: { content: string }) => {
	return (
		<div
			className="rounded-md bg-accent-700 px-3 py-2 text-base-900 dark:text-base-100"
			data-testid="toast"
		>
			<div className="text-sm">{props.content}</div>
		</div>
	)
}
